import { useEffect, useState } from 'react';
import { Tabs } from '@zoopla-eng/zds/tabs';
import { SearchMedium } from '@zoopla-eng/zds/icons';
import {
    LocationSearchInput,
    QueryType
} from '@zoopla/listing-search-location-search-input';
import { Box } from '@zoopla-eng/zds/box';
import { ContentfulImageWrapper } from '../../queries/heroContent.types';
import { content } from './constants';
import BackgroundImage from './HomepageHero.png';
import { ParsedUrlQuery } from 'querystring';
import { useRouter } from 'next/router';
import { triggerTracking } from '@zoopla/tracking';
import { ButtonLink, ButtonLeft } from '@zoopla-eng/zds/button';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import {
    locationSearchInputWrapperWrapperStyle,
    ctaContainerStyle,
    bgDefault,
    bgM100,
    bgS200,
    heroContainerStyle,
    heroImageStyle,
    heroSpacerStyle,
    heroWrapperStyle,
    subHeaderStyle,
    tabWrapperStyle,
    headlineStyle,
    buttonTextStyle,
    formContentStyle,
    buttonContainerStyle,
    locationSearchInputWrapperStyle
} from './TabbedSearch.styles.css';
import { fontGroups } from '@zoopla-eng/zds/themes';

const {
    missingSearchAreaError,
    searchAreaPlaceholder,
    saleQueryParams,
    rentQueryParams,
    housePricesQueryParams
} = content;

type TabContentProps = {
    searchForm: string;
};

const buildSearchUrl = (queryParams: ParsedUrlQuery) => {
    const searchPath = '/search/';
    const queryParamString = `?${Object.entries(queryParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
        .join('&')}`;

    return `${searchPath}${queryParamString}`;
};

const TabContent = ({ searchForm }: TabContentProps) => {
    const router = useRouter();
    const [submitted, setSubmitted] = useState(false);
    const [selectedSearchArea, setSelectedSearchArea] = useState<{
        id: string;
        text: string;
    } | null>({
        id: '',
        text: ''
    });
    const [searchAreaInputValue, setSearchAreaInputValue] = useState<
        string | undefined
    >();

    const searchValue = selectedSearchArea?.text || searchAreaInputValue || '';

    const isForSale = searchForm === 'for-sale';

    const handleSubmit = (event: React.SyntheticEvent) => {
        let queryParams;

        event.preventDefault();
        setSubmitted(true);

        if (!searchValue) {
            return;
        }

        if (isForSale) {
            queryParams = saleQueryParams;
        } else if (searchForm === 'to-rent') {
            queryParams = rentQueryParams;
        } else {
            queryParams = housePricesQueryParams;
        }

        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'search',
            eventLabel: `${searchForm}-success`
        });

        router.push(
            buildSearchUrl({
                ...queryParams,
                q: searchValue,
                geo_autocomplete_identifier: selectedSearchArea?.id ?? '',
                search_source: 'home'
            })
        );
    };

    return (
        <form onSubmit={handleSubmit} role="search">
            <div className={formContentStyle}>
                <div
                    className={locationSearchInputWrapperWrapperStyle({
                        type: 'notHalf'
                    })}
                >
                    <div
                        className={locationSearchInputWrapperStyle}
                        data-testid="autosuggest-search-input"
                    >
                        <LocationSearchInput
                            onSelectedItemChange={setSelectedSearchArea}
                            onInputValueChange={setSearchAreaInputValue}
                            label={'Enter a city, town or postcode'}
                            placeholder={searchAreaPlaceholder}
                            errorText={
                                submitted && !searchValue
                                    ? missingSearchAreaError
                                    : undefined
                            }
                            queryType={
                                searchForm === 'house-prices'
                                    ? QueryType.AddressPostcodes
                                    : QueryType.GeoLocations
                            }
                        />
                    </div>
                </div>
                <div
                    className={ctaContainerStyle({
                        type: 'notHalf'
                    })}
                >
                    <ButtonLeft
                        category="primary"
                        Icon={SearchMedium}
                        data-testid="search-btn"
                    >
                        Search
                    </ButtonLeft>
                </div>
            </div>
        </form>
    );
};

const HomeValueTabContent = () => {
    return (
        <Box
            fontGroup="ladle"
            px="S400"
            pt="S200"
            pb="S100"
            bpM100={{
                py: 'S100'
            }}
        >
            <div className={buttonContainerStyle}>
                <strong className={buttonTextStyle}>
                    First, let’s find your home to see how much it’s worth
                </strong>
                <ButtonLink
                    fw
                    category="primary"
                    data-testid="my-home-cta"
                    href="/my-home/?search_source=home"
                >
                    Find my home
                </ButtonLink>
            </div>
        </Box>
    );
};

type TabbedSearchProps = {
    image: ContentfulImageWrapper;
    brandHeadline: string;
    h1TitleTag: string;
    isLoggedIn?: boolean;
};

export const TabbedSearch = ({
    brandHeadline,
    image,
    h1TitleTag,
    isLoggedIn
}: TabbedSearchProps) => {
    const [value, updateValue] = useState('for-sale');
    const [homeValueFeature] = useOptimizelyFeatureFlag('_home-value-search');

    const homeValueTabIsEnabled = homeValueFeature === true;

    useEffect(() => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory: 'homepage',
            eventAction: 'channel_tab',
            eventLabel: `${value}`
        });
    }, [value]);

    const backgroundImageStyles = {
        default: `url(${
            image?.url ? `${image.url}?w=550&fm=webp&q=75&` : BackgroundImage
        })`,
        S200: `url(${
            image?.url ? `${image.url}?w=768&fm=webp&q=75&` : BackgroundImage
        })`,
        M100: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%),url(${
            image?.url ? `${image.url}?w=1350&fm=webp&q=75&` : BackgroundImage
        })`
    };

    return (
        <Box fw>
            <div className={heroWrapperStyle({ isLoggedIn })}>
                {!isLoggedIn && (
                    <div
                        className={heroImageStyle}
                        style={assignInlineVars({
                            [bgDefault]: backgroundImageStyles.default,
                            [bgS200]: backgroundImageStyles.S200,
                            [bgM100]: backgroundImageStyles.M100
                        })}
                    />
                )}
                <div className={heroContainerStyle({ isLoggedIn })}>
                    {!isLoggedIn && (
                        <p
                            className={`${headlineStyle} ${fontGroups.teapot}`}
                            data-testid="homepage-headline"
                        >
                            {brandHeadline
                                ? brandHeadline
                                : 'We know what a home is really worth'}
                        </p>
                    )}

                    <h1
                        className={`${subHeaderStyle({ isLoggedIn })} ${
                            isLoggedIn ? fontGroups.mug : fontGroups.whisk
                        }`}
                        data-testid="homepage-subheader"
                    >
                        {h1TitleTag
                            ? h1TitleTag
                            : 'Find homes to buy or rent and check house prices'}
                    </h1>
                    <div className={tabWrapperStyle({ isLoggedIn })}>
                        <Tabs
                            label="Houses"
                            value={value}
                            alignment={{ default: 'centered' }}
                            tabs={[
                                {
                                    value: 'for-sale',
                                    label: 'For sale',
                                    component: <TabContent searchForm={value} />
                                },
                                {
                                    value: 'to-rent',
                                    label: 'To rent',
                                    component: <TabContent searchForm={value} />
                                },
                                homeValueTabIsEnabled
                                    ? {
                                          value: 'home-value',
                                          label: 'Home value',
                                          component: <HomeValueTabContent />
                                      }
                                    : {
                                          value: 'house-prices',
                                          label: 'House prices',
                                          component: (
                                              <TabContent searchForm={value} />
                                          )
                                      }
                            ]}
                            onValueChange={updateValue}
                        />
                    </div>
                </div>
            </div>
            <div
                className={heroSpacerStyle({
                    isLoggedIn
                })}
            />
        </Box>
    );
};
